import { Form, Formik } from 'formik'
import first from 'lodash/first'
import slice from 'lodash/slice'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { object, string } from 'yup'

import { Button } from '@Components/Button'
import { UnstyledCSImage } from '@Components/CS/Image'
import { Modal, SharedModalProps } from '@Components/Modal'
import { ProductSizeGuide } from '@Components/Product/SizeGuide'
import { SizeMissing } from '@Components/SizeModal/SizeMissing'
import { SizeOutOfStock } from '@Components/SizeModal/SizeOutOfStock'
import { ModalWrapper, ProductImage, SelectorAndBuyButtonWrapper, StyledSizeSelector } from '@Components/SizeModal/styled'
import { SizeSelectorState } from '@Components/SizeSelector'
import { Product } from '@Generated/graphql'
import { useFilteredVariants } from '@Globals/Product/useFilteredVariants'
import { useTranslation } from '@Globals/Translations'

type SelectSizeFormDataFormat = {
  size: string
}

type SizeModalProps = SharedModalProps & {
  chooseOptionText: string
  onSelect: (selectedSize: string) => void
  preselectedID?: string
  product: Product
}

export const SizeModal: FC<SizeModalProps> = ({
  chooseOptionText,
  isOpen,
  onHide,
  onSelect,
  preselectedID = '',
  product,
}) => {
  const { t } = useTranslation()
  const [selectorState, setSelectorState] = useState<SizeSelectorState>(SizeSelectorState.IDLE)

  const { availableVariants: sizes } = useFilteredVariants({ product })

  const onSizeSelected = useCallback(({ size }: SelectSizeFormDataFormat) => {
    onSelect(size)
  }, [onSelect])

  const validationSchema = useMemo(
    () => object().shape({
      size: string()
        .required()
        .test(
          'test-if-default-string',
          t('cms:INPUT_FIELD_IS_REQUIRED'),
          value => !(!value || value === chooseOptionText),
        ),
    }),
    [chooseOptionText, t],
  )

  const primaryImage = product.assets?.find(asset => asset.tags?.includes('primary'))?.url ?? (product.assets || [])[0]?.url

  // If there is a size-guide, and the very first block is a title block, we'll remove it. Otherwise,
  // we'll end up with double headers, and that just looks weird.
  const sizeGuideBlocks = product.sizeGuide?.blocks || []
  const sizeGuideBlocksToRender = first(sizeGuideBlocks)?.type === 'titleBlock'
    ? slice(sizeGuideBlocks, 1)
    : sizeGuideBlocks

  return (
    <Modal isOpen={isOpen} onHide={onHide}>
      <Formik<SelectSizeFormDataFormat>
        initialValues={{ size: preselectedID }}
        onSubmit={onSizeSelected}
        validationSchema={validationSchema}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => (
          <>
            <Form id="chooseSizeForm" onSubmit={handleSubmit}>
              <ModalWrapper>
                <p className="modalTitle">{t('cms:USER_SELECT_OVERLAY_SIZE_TITLE')}</p>

                <ProductImage>
                  <UnstyledCSImage height={250} url={primaryImage} width={250} />
                </ProductImage>

                <p className="productTitle">{product.name}</p>

                <SelectorAndBuyButtonWrapper>
                  <StyledSizeSelector
                    error={Boolean(errors.size && touched.size)}
                    name="size"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onStateChange={setSelectorState}
                    sizes={sizes}
                    value={values.size}
                    withoutLabelAnimation
                  />
                  <Button
                    className="buy-button"
                    disabled={selectorState !== SizeSelectorState.SIZE_AVAILABLE}
                    type="submit"
                  >
                    {t('cms:ADD_TO_CART')}
                  </Button>
                </SelectorAndBuyButtonWrapper>
              </ModalWrapper>
            </Form>
            {selectorState !== SizeSelectorState.MY_SIZE_DOESNT_EXIST && sizeGuideBlocksToRender.length > 0 && (
              <ProductSizeGuide
                blocks={sizeGuideBlocksToRender}
                id="modal"
                title={product.sizeGuide?.title || ''}
              />
            )}
            {selectorState === SizeSelectorState.MY_SIZE_DOESNT_EXIST && (<SizeMissing onAbort={onHide} product={product} />)}
            {selectorState === SizeSelectorState.SIZE_SOLD_OUT && (<SizeOutOfStock onAbort={onHide} product={product} selectedSize={values.size} />)}
          </>
        )}
      </Formik>
    </Modal>
  )
}
